import { Container, TextField, Typography } from "@material-ui/core";
import { format, isBefore, startOfDay } from "date-fns";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { EditRequest, ArchiveTableRow } from "../../components";
import { getFirebaseRealTimeDatabase } from "../../services/firebaseRealTimeDatabase";
import { useStore } from "../../store";
import { KitchenOrder, Sector } from "../../types";
import useStyles from "./styles";
import {
  useFetchAllDailyOrders,
  useFetchAllOrdersForSchedule,
  useGetEntity,
  useGetSectorsActive,
} from "../../api/orders/hooks";
import { Select } from "../../components/Select";
import { useEntity } from "../../hooks/useEntity";
import { redirectToAdminLogin } from "../../utils/navigationUtils";
import {
  getScopedSectors,
  shouldShowSectorOthers,
} from "../../utils/sectorScope";

export const Archive: React.FC = () => {
  const Styles = useStyles();

  const [selectedDate, setSelectedDate] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const isPastOrder = (order: KitchenOrder) =>
    !order.isSchedule && (!order.createdAt || isBefore(new Date(order.createdAt), startOfDay(new Date())));

  const [entityId, setEntityId] = useState<string>("");
  const [orders, setOrders] = useState<any>([]);
  const [sectorsList, setSectorsList] = useState<any[]>([]);
  const [sectorId, setSectorId] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState<KitchenOrder | null>(null);
  const [sectorSelected, setSectorSelected] = useState("all");

  const [openModal, setOpenModal] = useState<boolean>(true);

  const { userManager, navigation } = useStore();

  interface ArchiveLocationState {
    order?: KitchenOrder;
    isArchive?: boolean;
    isEdit?: boolean;
  }

  type OrderType = "scheduled" | "realtime";

  const orderTypeOptions = [
    { label: "Agendamento", value: "scheduled" },
    { label: "Tempo real", value: "realtime" },
  ];

  const [orderType, setOrderType] = useState<OrderType>("realtime");

  const location = useLocation();
  const state = location.state as ArchiveLocationState | undefined;

  const { data: _orders, isLoading } = useFetchAllDailyOrders(entityId, true, selectedDate);
  const { data: allOrders, isLoading: isLoadingAllOrders } =
    useFetchAllOrdersForSchedule(entityId);
  const { data: sectors } = useGetSectorsActive(entityId, true);
  const scopedSectors = useMemo(
    () => getScopedSectors(sectors, userManager.segmentId),
    [sectors, userManager.segmentId],
  );
  const hasFixedSegment = !!userManager.segmentId;
  const { data: entity } = useGetEntity(entityId, true);
  const { showSectors } = useEntity();

  const handleOpenModal = (order: KitchenOrder) => {
    if (!isPastOrder(order) && entity?.managerAuthorization) {
      redirectToAdminLogin(navigation, order, { isArchive: true });
    } else {
      setSelectedOrder(order);

      setOpenModal(true);
    }
  };

  useEffect(() => {
    if (state?.order && state?.isArchive && state?.isEdit) {
      setSelectedOrder(state.order);
      setOpenModal(true);
    }
  }, [state?.order, state?.isArchive, state?.isEdit]);

  const handleCloseModal = () => {
    setOpenModal(false);

    setSelectedOrder(null);
  };

  const filterOrdersBySector = (order: any) => {
    if (hasFixedSegment) {
      return order.items?.some(
        (item: any) => item.segment?.id === userManager.segmentId,
      );
    }

    if (sectorId === "all") return true;

    if (sectorId === "others") {
      return order.items?.some((item: any) => !item.segment);
    }

    return order.items?.some((item: any) => item.segment?.id === sectorId);
  };

  const loadOrders = async () => {
    if (!_orders) {
      setOrders([]);
    } else {
      const ordersToSet =
        orderType === "scheduled"
          ? allOrders || []
          : _orders.filter((order: any) => !order.isSchedule);

      setOrders(
        ordersToSet
          .sort(
            (firstOrder: any, secondOrder: any) =>
              new Date(secondOrder.createdAt).getTime() -
              new Date(firstOrder.createdAt).getTime(),
          )
          .filter((order: any) => filterOrdersBySector(order)),
      );
    }
  };

  const formatList = () => {
    const listSectors = scopedSectors?.map((_sector: Sector) => ({
      label: _sector.name,
      value: _sector.id,
    }));

    const listSortedSectors: any[] = listSectors?.sort((a: any, b: any) =>
      a.label.localeCompare(b.label),);

    if (!hasFixedSegment) {
      listSortedSectors.unshift({ label: "Todos", value: "all" });
    }
    if (shouldShowSectorOthers(userManager.segmentId)) {
      listSortedSectors.push({ label: "Outros", value: "others" });
    }

    setSectorsList(listSortedSectors);
  };

  const filterOrders = (id: string) => {
    setSectorSelected(id);
    setSectorId(id);
    loadOrders();
  };

  useEffect(() => {
    getFirebaseRealTimeDatabase(userManager.entity, loadOrders);
  }, []);

  useEffect(() => {
    (async () => {
      await loadOrders();
    })();
  }, [_orders, allOrders, sectorId]);

  useEffect(() => {
    loadOrders();
  }, [orderType]);

  useEffect(() => {
    if (userManager.entity) {
      setEntityId(userManager.entity);
    }
  }, [userManager]);

  useEffect(() => {
    if (scopedSectors?.length) {
      formatList();
    }
  }, [scopedSectors, hasFixedSegment, userManager.segmentId]);

  useEffect(() => {
    if (hasFixedSegment && userManager.segmentId) {
      setSectorSelected(userManager.segmentId);
      setSectorId(userManager.segmentId);
    }
  }, [hasFixedSegment, userManager.segmentId]);

  return (
    <Container maxWidth={false} className={Styles.container}>
      <div className={Styles.table}>
        <div className={Styles.containerFilter}>
          {orderType === "realtime" && (
            <div className={Styles.filterSector}>
              <Typography component="label" htmlFor="archive-order-date" className={Styles.filterLabel}>
                Data dos pedidos
              </Typography>
              <TextField
                id="archive-order-date"
                className={Styles.dateField}
                variant="outlined"
                fullWidth
                type="date"
                value={selectedDate}
                inputProps={{ max: format(new Date(), "yyyy-MM-dd") }}
                onChange={(event) => {
                  const date = event.target.value;
                  if (date && date <= format(new Date(), "yyyy-MM-dd")) {
                    handleCloseModal();
                    setOrders([]);
                    setSelectedDate(date);
                  }
                }}
              />
            </div>
          )}
          {sectorsList?.length > 0 && (showSectors || hasFixedSegment) && (
            <>
              <div className={Styles.filterSector}>
                <Select
                  label="Setor"
                  options={sectorsList}
                  value={sectorSelected}
                  onChange={(val) => filterOrders(val)}
                />
              </div>
            </>
          )}
          <div className={Styles.filterSector}>
            <Select
              label="Tipo do pedido"
              options={orderTypeOptions}
              value={orderType}
              onChange={(val) => setOrderType(val)}
            />
          </div>
        </div>
        {orderType === "realtime" && selectedDate < format(new Date(), "yyyy-MM-dd") && (
          <Typography>Pedidos de dias anteriores são apenas para consulta.</Typography>
        )}
        {_orders === false && <Typography>Não foi possível carregar os pedidos.</Typography>}
        <hr className={Styles.line} />
        <ArchiveTableRow header />
        {(isLoading || isLoadingAllOrders) && (
          <div className={Styles.tableBody}>
            <div className={Styles.tableRow}>
              <Typography
                noWrap
                className={`${Styles.tableCell} ${Styles.tableColumn2}`}
              >
                Loading data...
              </Typography>
            </div>
          </div>
        )}
        {!isLoading && !isLoadingAllOrders && (
          <div className={Styles.tableBody}>
            {orders.map((order: any) => (
              <ArchiveTableRow
                key={order.id}
                data={order}
                readOnly={isPastOrder(order)}
                selectOrder={() => handleOpenModal(order)}
              />
            ))}
          </div>
        )}
      </div>

      {selectedOrder && (
        <EditRequest
          order={selectedOrder}
          open={openModal}
          onClose={handleCloseModal}
          readOnly={isPastOrder(selectedOrder)}
          disableProductsActionButtons
        />
      )}
    </Container>
  );
};
